import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../../api/api-caller';
import initialState, { Data, ElementsListState } from '../elementsList-state';

type APIData = {
  id: string;
  name: string;
  typename: string;
  informationItemIds: string;
  link: string;
  title?: string;
};

const fetchElementData = async (item: ElementsListViewModelProps): Promise<ApiResult> => {
  const queryString = `@relatedElements = ${item.query};
@documents = @relatedElements.informationitems;
@ = @relatedElements;
@ += @documents;`;

  const { itemTitleAttribute } = item;
  const attributes = itemTitleAttribute
    ? `name,${itemTitleAttribute}(as="title"),typename,infoitem.linklocation(as=link), functionasattribute(functionasattribute="informationitems", as="informationItemIds")`
    : `name,typename,infoitem.linklocation(as=link), functionasattribute(functionasattribute="informationitems", as="informationItemIds")`;
  const query: ApiRequest = {
    body: {
      query: queryString,
      attributes,
      criteria: '',
      sortBy: item.sortBy || ''
    },
    method: 'POST'
  };
  const result: ApiResult = await api.asyncCallApi('meaquery', query);

  return result;
};

const getElementData = (result: ApiResult): APIData[] => {
  const { data } = result;

  return data;
};

const errorHandler = (apiResult: ApiResult): ElementsListState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const transformData = (dataset: APIData[]): Data[] => {
  const allNonInfoItems = dataset.filter(
    (dataitem: APIData) => dataitem.typename !== 'Information Item'
  );
  const allInfoItems = dataset.filter(
    (dataitem: APIData) => dataitem.typename === 'Information Item'
  );

  const finalData: Data[] = [];

  allNonInfoItems.forEach((item: APIData) => {
    if (item.informationItemIds.length > 0) {
      const documentsForItem = allInfoItems.filter((dataitem: APIData) =>
        item.informationItemIds.includes(dataitem.id)
      );

      const childInfoItems: Data[] = [];
      documentsForItem.forEach((document: APIData) => {
        childInfoItems.push({ name: document.name, id: document.id, link: document.link });
      });
      finalData.push({
        name: item.name,
        id: item.id,
        informationItems: childInfoItems,
        title: item.title
      });
    } else {
      finalData.push({ name: item.name, id: item.id, title: item.title });
    }
  });

  return finalData;
};

const doGetData = async (item: ElementsListViewModelProps): Promise<ElementsListState> => {
  try {
    const result = await fetchElementData(item);

    if (!result.ok) {
      return errorHandler(result);
    }

    const elementData: APIData[] = getElementData(result);

    const transformedData = transformData(elementData);

    return {
      ...initialState,
      data: transformedData
    };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface ElementsListViewModelType {
  fetchData(): Promise<ElementsListState>;
  getData(): Promise<ElementsListState>;
}

interface ElementsListViewModelProps {
  query: string;
  itemTitleAttribute?: string;
  sortBy?: string;
}

const createElementsListViewModel = (
  item: ElementsListViewModelProps
): ElementsListViewModelType => {
  let data: ElementsListState;
  const cacheKey: string = JSON.stringify(item);

  const fetchData = async (): Promise<ElementsListState> => {
    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = await doGetData(item);
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getData = async (): Promise<ElementsListState> => {
    if (data) {
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    fetchData,
    getData
  };
};

export default createElementsListViewModel;
